import html from "csi/html";
import types from "csi/types";
import * as _ from "lodash";
import {
	COLOR_KEY,
	COLUMNS,
	DEFAULT_COLOR,
	DEFAULT_FONT_FAMILY,
	DEFAULT_FONT_SIZE,
	DEFAULT_PLACEHOLDER_SHAPE_PROPERTIES,
	FONTSIZE_KEY,
	GUIDE_SNAP_DIFF,
	HEADING_TAG_FONT_MAP,
	HIGHLIGHT_COLOR,
	HIGHLIGHT_ID,
	IMAGE_PLACEHOLDER_FILL_COLOR,
	METADATA,
	MIN_DIMENSION,
	PARAGRAPH_KEY,
	PERCENTAGE_HUNDRED,
	PLACEHOLDER_DISABLED_SPAN,
	PLACEHOLDER_LABEL_SPAN,
	PLACEHOLDER_RICH_TEXT_DIV,
	PLACEHOLDER_VALUE_SPAN,
	PRESENT,
	ROWS,
	SELECTION_STATE,
	SHAPE_DRAW_METADATA,
	TABLE_DIMENSIONS,
	TAG_STYLES,
	ZERO_TEXT
} from "../../Constants";
import { presetGeometries } from "../../presetShapesMeta/all";
import { predefinedGuides } from "../../presetShapesMeta/PredefinedGuides";
import { star5 } from "../../presetShapesMeta/star5";
import { getStore } from "../../store/ReduxRegistry";
import { IDragBoxProperties } from "../../widgets/components/DragSelectionBox";
import { IExtents } from "../../widgets/interfaces/model/dataStructures/Extents";
import { IOffset } from "../../widgets/interfaces/model/dataStructures/Offset";
import {
	AutosizeMode,
	FillTypes,
	GeometryTypes,
	IPageObjectTypes,
	PathOperationTypes,
	ShapeType
} from "../../widgets/interfaces/model/enumerations/index";
import { PageObjectScalePointType } from "../../widgets/interfaces/model/enumerations/PageObjectScalePoints";
import { PlaceholderTextFormat } from "../../widgets/interfaces/model/enumerations/PlaceholderTextFormat";
import { ICustomGeometry } from "../../widgets/interfaces/model/geometry/CustomGeometry";
import { IPresetGeometry } from "../../widgets/interfaces/model/geometry/PresetGeometry";
import { IShapeGuide } from "../../widgets/interfaces/model/geometry/ShapeGuide";
import { IDrawLineTo, IMovePathTo, IShapePath, PathOperation } from "../../widgets/interfaces/model/geometry/ShapePath";
import { IShapePositionCoordinate } from "../../widgets/interfaces/model/geometry/ShapePositionCoordinate";
import { ICanvasEditorState, IPage } from "../../widgets/interfaces/model/index";
import { IGroupShape } from "../../widgets/interfaces/model/PageObjects/index";
import { IShape } from "../../widgets/interfaces/model/PageObjects/Shape";
import { IPlaceholderFillData } from "../../widgets/interfaces/model/PlaceholderFillData";
import { IConnectionShapeProperties } from "../../widgets/interfaces/model/properties/ConnectionShapeProperties";
import {
	IGroupShapeProperties,
	IPageObjectProperties,
	IPictureFillProperties,
	IShapeProperties,
	ITransform2D
} from "../../widgets/interfaces/model/properties/index";
import { PageObjectType } from "../../widgets/interfaces/model/Types";
import { IPosition } from "../../widgets/meta/Drag/CsiDragController";
import { angleInDegree, getArcEndPoint, _getValue } from "../../widgets/selectors/geometry";
import { evaluateFormula } from "../../widgets/selectors/index";
import { getDefaultTextPlaceholderHTML, getPlaceholderRichTextElement } from "../../widgets/util";
import { IAddImageRequest } from "./PageOperations";

const _c8DataStore: object = {};
let _c8Functions: IC8Functions;

export interface IDisplayCustomView {
	(attributeKeys: string[], groupObject: IGroupShape): void;
}

export interface IDisplayNameFunc {
	(key: string): string;
}

export interface IDisplayTypeInstancesFunc {
	(key: string | undefined): void;
}

export interface IC8Functions {
	displayCustomView?: IDisplayCustomView;
	getDisplayNameForKey?: IDisplayNameFunc;
	getDisplayNameForType?: IDisplayNameFunc;
	getStartTypeForKey?: IDisplayNameFunc;
	localizeString?: IDisplayNameFunc;
	displayTypeInstances?: IDisplayTypeInstancesFunc;
}

/**
 * Get and return data from the C8 datastore.
 * @param path { string } path of data in datastore (foo.bar.baz)
 * @returns desired data or entire datastore
 */
export const getC8Data = (path?: string) => {
	if (path) {
		// get the data specified by the path
		return _.get(_c8DataStore, path);
	} else {
		// get the entire datastore if no args were passed
		return _c8DataStore;
	}
};

/**
 * Set data in the datastore.
 * @param path { string } path of data in datastore (foo.bar.baz)
 * @param data { any } data to store in the datastore at the path
 */
export const setC8Data = (path: string, data: any) => {
	if (path) {
		// first get the object at the path, or use empty object
		const target = getC8Data(path) || {};

		// now add new properties to that object
		_.extend(target, data);

		// set the data at the path
		_.set(_c8DataStore, path, target);
	}
};

/**
 * Set c8 functions in the datastore.
 * @param functions { IC8Functions }
 * array of c8 functions to access from c8-canvas application
 */
export const setC8Functions = (functions: IC8Functions) => {
	_c8Functions = functions;
};

/**
 * Rect box interface
 */
export interface IRectBox {
	top: number;
	right: number;
	bottom: number;
	left: number;
};

/**
 * Interface for rectangle with size
 */
export interface IRectSizeBox extends IRectBox {
	width: number;
	height: number;
};

/**
 * Shape box interface
 */
export interface IShapeBox {
	x: number;
	y: number;
	width: number;
	height: number;
};

/**
 * PageObject resize meta. It provides changes in Offset & Extents for shape & cropped image.
 * All values are in ratio with respect to width / height.
 */
export interface IResizeMeta {
	dX: number;
	dY: number;
	dWidth: number;
	dHeight: number;
	cropDX: number;
	cropDY: number;
	cropDWidth: number;
	cropDHeight: number;
};

/**
 * Returns a boolean, whether the target object can have text or not.  Object parameter
 * should be a page object.
 */
export const allowsText = (obj: any) => {
	if (obj.spPr && obj.spPr.geometry && obj.spPr.geometry.type === "CUSTOM") {
		return false;
	}
	return obj.type !== "CONNECTION_SHAPE" && !obj.isPicture;
};

/**
 * Utility function to return xor of two booleans. Used with flipping.
 */
export const xor = (a: boolean, b: boolean) => {
	if (a && b) {
		return false;
	} else if (!a && !b) {
		return false;
	} else {
		return true;
	}
};

/**
 * Generates new UUIDs for all objects and children
 * @param pageObjects array of objects to update
 */
export const deepGenerateNewUUIDs = (pageObjects: PageObjectType[]) => {
	_.each(pageObjects, (pageObject) => {
		const groupObject = pageObject as IGroupShape;
		groupObject.shapeId = generateUUID();
		if (groupObject.childObjects) {
			deepGenerateNewUUIDs(groupObject.childObjects);
		}
	});
};

export const getIndexedChildren = (array: any[], indices: number[]) => {
	const result: any[] = [];

	indices.forEach((value) => {
		result.push(array[value]);
	});

	return result;
};

/**
 * Get matrix with applied given rotation and flip
 * @param rot The rotation angle
 * @param flipH The Horizontal flip flag
 * @returns Matrix with rotation and flip applied
 */
export const getRotationFlipMatrix = (rot: number, flipH: boolean) => {
	return new DOMMatrix()
		.rotate(rot || 0)
		.scale(flipH ? -1 : 1, 1);
};

/**
 * Get matrix for object
 * @param pageObject The pageObject for which matrix to be formed
 * @returns pageObject matrix
 */
export const getMatrixForObject = (pageObject: PageObjectType): DOMMatrix => {
	const { rot, flipH } = pageObject.spPr.xfrm;
	return getRotationFlipMatrix(rot, flipH);
};

export const mapGroupedPointToShapeMatrix =
	(
		groupObject: PageObjectType | undefined, pageObject: PageObjectType,
		point: IPosition, doInverse?: boolean,
	): IPosition => {
		const ungroupedObject = _.cloneDeep(pageObject);

		if (groupObject) {
			ungroupedObject.spPr.xfrm.flipH = xor(ungroupedObject.spPr.xfrm.flipH, groupObject.spPr.xfrm.flipH);
			ungroupedObject.spPr.xfrm.rot += groupObject.spPr.xfrm.rot;
		}

		return mapPointToShapeMatrix(ungroupedObject, point, doInverse);
	};

/**
 * Find the page object with the given Id no matter how deep in groups it is
 * @param pageObjects array of objects to find the shape in
 * @param id the id of the shape
 * @returns an object containing two parameters: the array containing the target object and the
 *          position in that array of the target object.  This allows the object to be easily
 *          replaced.
 */
export const deepFindObjectById = (pageObjects: PageObjectType[], id: String): any => {
	for (let x = 0; x < pageObjects.length; x++) {
		const groupObject = pageObjects[x] as IGroupShape;
		if (pageObjects[x].shapeId === id) {
			return { pageObjects, x };
		} else if (groupObject.childObjects) {
			const searchResult = deepFindObjectById(groupObject.childObjects, id);
			if (searchResult) {
				return searchResult;
			}
		}
	}
	return null;
};

/**
 * Find the keys contained in the group
 * @param group the card to get the keys from
 * @returns array of string consisting of the keys needed for the group
 */
export const getKeysFromGroup = (group: IGroupShape | IShape): any => {
	const keys = [];
	if (!group.hasOwnProperty("childObjects")) {
		group = group as IShape;
		keys.push(group.attributeKey);
	} else {
		group = group as IGroupShape;
		for (const childObject of group.childObjects) {
			const child = childObject as IShape;
			keys.push(child.attributeKey);
		}
	}
	return keys;
};

/**
 * Find the group containing a shape with the given id
 * @param pageObjects array of objects to find the shape in
 * @param id the id of the shape
 * @param currentGroup used in recursion, the current outermost group
 * @returns the group object to fill
 */
export const deepFindContainingGroup = (
	pageObjects: PageObjectType[],
	id: String, currentGroup: IGroupShape | null,
): any => {
	let x = 0;
	while (x < pageObjects.length) {
		const pageObject = pageObjects[x] as IGroupShape;

		if (pageObjects[x].shapeId === id) {
			return currentGroup;
		} else if (pageObject.childObjects) {
			const searchResult = deepFindContainingGroup(pageObject.childObjects, id, pageObject);
			if (searchResult) {
				return searchResult;
			}
		}
		x = x + 1;
	}
	return null;
};

/**
 * Find the outermost selectable object containing the shape with the given id
 * @param pageObjects array of objects to find the shape in
 * @param id the id of the shape
 * @param selectedObject used in recursion, the index of the outermost object we have found to select
 * @param groupSelectionIndices used in recursion, the index of the current group we have found to select
 * @returns an object containing the index of the base-level object to be selected and the group selection
 *          index, if one is necessary.  Essentially the coordinates to pass to a selectObject action.
 */
export const deepFindOutermostGroup = (
	pageObjects: PageObjectType[], id: String, selectedObject: Number = -1,
	groupSelectionIndex: Number = -1,
): any => {
	for (let x = 0; x < pageObjects.length; x++) {
		const go = pageObjects[x] as IGroupShape;
		let checkingObject = selectedObject;
		let checkingSelectionIndex = groupSelectionIndex;

		if (selectedObject === -1) {
			// In this case we are checking the outermost layer and find the object, so just select it.
			checkingObject = x;
		} else if (groupSelectionIndex === -1) {
			// In this case we are 1 layer deep, select the object as a subgroup element
			checkingSelectionIndex = x;
		}

		if (pageObjects[x].shapeId === id) {
			const checkingSelectionIndices = [];
			if (checkingSelectionIndex !== -1) {
				checkingSelectionIndices.push(checkingSelectionIndex);
			}
			return { checkingObject, checkingSelectionIndices };
		} else if (go.childObjects) {
			const searchResult = deepFindOutermostGroup(go.childObjects, id, checkingObject, checkingSelectionIndex);
			if (searchResult) {
				return searchResult;
			}
		}
	}
	return null;
};

/**
 * Map point to given matrix
 * @param matrix The matrix for mapping
 * @param point The point to be mapped
 * @param doInverse  Flag ot indicate if matrix inverse to apply. Defaults to true.
 */
export const mapPointToMatrix = (matrix: DOMMatrix, point: IPosition, doInverse = true): IPosition => {
	const pointMatrix = new DOMMatrix()
		.translate(point.x, point.y);
	if (doInverse) {
		matrix = matrix.inverse();
	}
	const correctedMatrix = matrix.multiply(pointMatrix);
	return { x: correctedMatrix.e, y: correctedMatrix.f };
};

/**
 * Map point to pageObject (shape) matrix.
 * @param pageObject The pageObject (shape | groupShape | picture )
 * @param point The point to be mapped
 * @param doInverse Flag to indicate if matrix inverse to apply. If not provided, algorithm assumes it to be true.
 * @returns Mapped point to page object matrix
 */
export const mapPointToShapeMatrix = (pageObject: PageObjectType, point: IPosition, doInverse?: boolean): IPosition => {
	const shapeMatrix = getMatrixForObject(pageObject);
	return mapPointToMatrix(shapeMatrix, point, doInverse);
};

/**
 * Given a page object, get it's dimensions in terms of points.
 * A page object has its boundary defined by offsets and extents.
 * This method converts the form into array of four coordinates.
 * This method also takes rotation angle into consideration while computing coordinates.
 * @param po The page object
 * @returns Array of four coordinates for page objects
 */
export const getDimensions = (po: PageObjectType) => {
	const angle = po.spPr.xfrm.rot || 0;
	const { x, y } = po.spPr.xfrm.off;
	const { cx, cy } = po.spPr.xfrm.ext;
	const flip = po.spPr.xfrm.flipH;

	// Center of page object
	const center = { x: x + (cx / 2), y: y + (cy / 2) };

	// Page object's computational radius, considering its four points on a circle.
	const radius = Math.sqrt(((cx / 2) * (cx / 2)) + ((cy / 2) * (cy / 2)));

	// Angle made by diagonal to left side (top-left bottom-left) of page object
	const theta: number = angleInDegree(Math.atan(cx === 0 ? 0 : cy / cx));

	// Angle for top right point
	const topRightAngle = flip ? 180 + theta : 0 - theta;
	// Angle for bottom right point
	const bottomRightAngle = flip ? 180 - theta : 0 + theta;
	// Angle for bottom left point
	const bottomLeftAngle = flip ? 0 + theta : 180 - theta;
	// Angle for top left point
	const topLeftAngle = flip ? 0 - theta : 180 + theta;

	// Top-right coordinate
	const topRight = getArcEndPoint(center.x, center.y, radius, radius, angle + topRightAngle);
	// Bottom-right coordinate
	const bottomRight = getArcEndPoint(center.x, center.y, radius, radius, angle + bottomRightAngle);
	// Bottom-left coordinate
	const bottomLeft = getArcEndPoint(center.x, center.y, radius, radius, angle + bottomLeftAngle);
	// Top-left coordinate.
	// It should match page object's offsets when rotation angle is zero.
	const topLeft = getArcEndPoint(center.x, center.y, radius, radius, angle + topLeftAngle);

	return [topRight, bottomRight, bottomLeft, topLeft];
};

const _scaleObject = (
	po: PageObjectType, scalePointType: PageObjectScalePointType, delta: IPosition, bCenter: boolean,
	isImageScale = false, isMappingNeeded: boolean, aspectRatio: number, groupObject: IGroupShape | undefined,
)
	: IResizeMeta => {
	let dX: number = 0;
	let dY: number = 0;
	let cropDX = 0;
	let cropDY = 0;
	let dWidth: number = 0;
	let dHeight: number = 0;
	let mappedDelta = delta;

	if (isMappingNeeded) {
		if (groupObject) {
			mappedDelta = mapGroupedPointToShapeMatrix(groupObject, po as PageObjectType, delta, true);
		} else {
			mappedDelta = mapPointToShapeMatrix(po as PageObjectType, delta, true);
		}
	}

	// Flip and rotation under consideration for mapping crop delta
	let flip = false;
	let rot = 0;
	let srcRect = undefined;
	let refExt = po.spPr.xfrm.ext;

	if (po.type === IPageObjectTypes.SHAPE && po.isPicture) {
		srcRect = (po.spPr.fill as IPictureFillProperties).srcRect;
		refExt = refExt || srcRect.refExt;
		flip = srcRect.flipH;
		rot = srcRect.rot;
	}

	let cropDelta = delta;
	if (isMappingNeeded) {
		let adjRot = rot;
		let adjFlip = flip;
		if (groupObject) {
			adjRot += groupObject.spPr.xfrm.rot;
			adjFlip = xor(flip, groupObject.spPr.xfrm.flipH);
		}
		cropDelta = mapPointToMatrix(getRotationFlipMatrix(adjRot, adjFlip), delta, true);
	}

	// If cropping image is being scaled, then keep crop delta as mapped delta, being in same frame of reference.
	if (isImageScale) {
		mappedDelta = cropDelta;
	}

	let newDelta = mapPointToShapeMatrix(po as PageObjectType, mappedDelta, false);

	switch (scalePointType) {
		case PageObjectScalePointType.CORNER_LEFT_TOP: // left,top corner
			dX = bCenter ? mappedDelta.x : (newDelta.x + mappedDelta.x) / 2;
			cropDX = (cropDelta.x + mappedDelta.x) / 2;
			dWidth = (bCenter ? -2 : -1) * mappedDelta.x;
			dY = bCenter ? mappedDelta.y : (newDelta.y + mappedDelta.y) / 2;
			cropDY = (cropDelta.y + mappedDelta.y) / 2;
			dHeight = (bCenter ? -2 : -1) * mappedDelta.y;
			break;
		case PageObjectScalePointType.CORNER_RIGHT_TOP: // right,top corner
			dX = bCenter ? -1 * mappedDelta.x : (newDelta.x - mappedDelta.x) / 2;
			cropDX = bCenter ? -1 * mappedDelta.x : (cropDelta.x - mappedDelta.x) / 2;
			dWidth = (bCenter ? 2 : 1) * mappedDelta.x;
			dY = bCenter ? mappedDelta.y : (newDelta.y + mappedDelta.y) / 2;
			cropDY = (cropDelta.y + mappedDelta.y) / 2;
			dHeight = (bCenter ? -2 : -1) * mappedDelta.y;
			break;
		case PageObjectScalePointType.CORNER_RIGHT_BOTTOM: // right, bottom corner
			dX = bCenter ? -1 * mappedDelta.x : (newDelta.x - mappedDelta.x) / 2;
			cropDX = bCenter ? -1 * mappedDelta.x : (cropDelta.x - mappedDelta.x) / 2;
			dWidth = (bCenter ? 2 : 1) * mappedDelta.x;
			dY = bCenter ? -1 * mappedDelta.y : (newDelta.y - mappedDelta.y) / 2;
			cropDY = bCenter ? -1 * mappedDelta.y : (cropDelta.y - mappedDelta.y) / 2;
			dHeight = (bCenter ? 2 : 1) * mappedDelta.y;
			break;
		case PageObjectScalePointType.CORNER_LEFT_BOTTOM: // left, bottom corner
			dX = bCenter ? mappedDelta.x : (newDelta.x + mappedDelta.x) / 2;
			cropDX = (cropDelta.x + mappedDelta.x) / 2;
			dWidth = (bCenter ? -2 : -1) * mappedDelta.x;
			dY = bCenter ? -1 * mappedDelta.y : (newDelta.y - mappedDelta.y) / 2;
			cropDY = bCenter ? -1 * mappedDelta.y : (cropDelta.y - mappedDelta.y) / 2;
			dHeight = (bCenter ? 2 : 1) * mappedDelta.y;
			break;
		case PageObjectScalePointType.SIDE_TOP: // top side center point
			mappedDelta.x = 0;
			newDelta = mapPointToShapeMatrix(po as PageObjectType, mappedDelta, false);

			dY = bCenter ? mappedDelta.y : (newDelta.y + mappedDelta.y) / 2;
			cropDY = (cropDelta.y + mappedDelta.y) / 2;
			dWidth = 0;
			dX = bCenter ? 0 : (newDelta.x - mappedDelta.x) / 2;
			cropDX = 0;
			dHeight = (bCenter ? -2 : -1) * mappedDelta.y;
			break;
		case PageObjectScalePointType.SIDE_RIGHT: // right side center point
			mappedDelta.y = 0;
			newDelta = mapPointToShapeMatrix(po as PageObjectType, mappedDelta, false);

			dX = bCenter ? -1 * mappedDelta.x : (newDelta.x - mappedDelta.x) / 2;
			cropDX = bCenter ? -1 * mappedDelta.x : (cropDelta.x - mappedDelta.x) / 2;
			dWidth = bCenter ? 2 * mappedDelta.x : mappedDelta.x;
			dY = bCenter ? 0 : (newDelta.y - mappedDelta.y) / 2;
			cropDY = 0;
			dHeight = 0;
			break;
		case PageObjectScalePointType.SIDE_BOTTOM: // bottom side center point
			mappedDelta.x = 0;
			newDelta = mapPointToShapeMatrix(po as PageObjectType, mappedDelta, false);

			dX = bCenter ? 0 : (newDelta.x - mappedDelta.x) / 2;
			cropDX = 0;
			dWidth = 0;
			dY = bCenter ? -1 * mappedDelta.y : (newDelta.y - mappedDelta.y) / 2;
			cropDY = bCenter ? -1 * mappedDelta.y : (cropDelta.y - mappedDelta.y) / 2;
			dHeight = (bCenter ? 2 : 1) * mappedDelta.y;
			break;
		case PageObjectScalePointType.SIDE_LEFT: // left side center point
			mappedDelta.y = 0;
			newDelta = mapPointToShapeMatrix(po as PageObjectType, mappedDelta, false);

			dX = bCenter ? mappedDelta.x : (newDelta.x + mappedDelta.x) / 2;
			cropDX = (cropDelta.x + mappedDelta.x) / 2;
			dWidth = (bCenter ? -2 : -1) * mappedDelta.x;
			dY = bCenter ? 0 : (newDelta.y - mappedDelta.y) / 2;
			cropDY = 0;
			dHeight = 0;
			break;
		default:
			break;
	}
	// Calculate deltaHeight/deltaWidth in ratio of ext.cx/ext.cy, if cx or cy is zero
	// taking it as minimum dimension 1 for handling "Divide by zero" (NaN) condition.
	// Although cx or cy can be in between 0 or 1, If so, then actual cx or cy will be considered.
	dWidth = roundNumber(dWidth / (po.spPr.xfrm.ext.cx || MIN_DIMENSION));
	dHeight = roundNumber(dHeight / (po.spPr.xfrm.ext.cy || MIN_DIMENSION));
	dX = roundNumber(dX / (po.spPr.xfrm.ext.cx || MIN_DIMENSION));
	dY = roundNumber(dY / (po.spPr.xfrm.ext.cy || MIN_DIMENSION));

	// Considering model shape boundary are different from render shape boundary (Ex. Callout Shape Image ) so
	// an explicit property to hold the cropDWidth and cropDHeight.
	const cropDWidth = dWidth;
	const cropDHeight = dHeight;

	return { dX, dY, dWidth, dHeight, cropDX, cropDY, cropDWidth, cropDHeight };
};

/**
 * Dimensions manipulator
 * Holds utility methods for various dimension manipulation
 */
export const manipulateDimensions = {
	/**
	 * Apply aspect ratio and recalculate delta
	 * @param ext Extents of the page object.
	 * @param scalePointType Scale point type. Required to deduce object resize strategy
	 * @param dWidth Delta width
	 * @param dHeight Delta height
	 * @param aspectRatio Aspect ratio of shape width and height
	 * @returns { IPosition } delta after applying aspect ratio
	 */
	applyAspectRatio: (
		increaseAmountExt: IExtents, ext: IExtents, scalePointType: PageObjectScalePointType,
		dWidth: number, dHeight: number, aspectRatio: number,
	)
		: IPosition => {
		const { cx, cy } = ext;
		const newWidth = cx + dWidth * (Math.round(increaseAmountExt.cx) || MIN_DIMENSION);
		const newHeight = cy + dHeight * (Math.round(increaseAmountExt.cy) || MIN_DIMENSION);
		let dX = 0;
		let dY = 0;
		let widthAfterAspectRatio = newWidth;
		let heightAfterAspectRatio = newHeight;

		if (newWidth > newHeight) {
			heightAfterAspectRatio = newWidth / aspectRatio;
		} else {
			widthAfterAspectRatio = newHeight * aspectRatio;
		}

		switch (scalePointType) {
			case PageObjectScalePointType.CORNER_RIGHT_BOTTOM: // right, bottom corner
				dX = (widthAfterAspectRatio - cx);
				dY = (heightAfterAspectRatio - cy);
				break;
			case PageObjectScalePointType.CORNER_LEFT_BOTTOM: // left, bottom corner
				dX = (widthAfterAspectRatio - cx) * -1;
				dY = (heightAfterAspectRatio - cy);
				break;
			case PageObjectScalePointType.CORNER_RIGHT_TOP: // right,top corner
				dX = (widthAfterAspectRatio - cx);
				dY = (heightAfterAspectRatio - cy) * -1;
				break;
			case PageObjectScalePointType.CORNER_LEFT_TOP: // left,top corner
				dX = (widthAfterAspectRatio - cx) * -1;
				dY = (heightAfterAspectRatio - cy) * -1;
				break;
			default:
				break;
		}
		return { x: dX, y: dY };
	},

	/**
	 * Given a group, find points such that minimum offset and maximum extents are formed based on its child objects.
	 * @param group group shape
	 * @returns Object containing minimum offset and maximum extents manipulated against given set of page objects.
	 */
	calculateGroupExtents: (group: IGroupShape): { off: IOffset; ext: IExtents } => {
		const processedPageObjects = _.map(group.childObjects, _.cloneDeep);

		// Create an array of children of the group, giving each its offset and rotation relative to the page
		_.each(processedPageObjects, (object) => {
			const { offX, offY } = manipulateDimensions.calculateSubgroupAbsoluteOffsets(object.spPr, group.spPr);

			object.spPr.xfrm.off.x = offX;
			object.spPr.xfrm.off.y = offY;
		});

		// Using the array of absolutely-positioned objects, calculate the new extents of the group
		return manipulateDimensions.forMinOffsetsMaxExtents(processedPageObjects);
	},

	/**
	 * Given an object and the group it's a part of, calculate its absolute position on the page
	 * @param spPr properties of the object whose position to calculate
	 * @param groupSpPr properties of the group containing the object
	 * @returns Object containing absolute offset and rotation of the page object
	 */
	calculateSubgroupAbsoluteOffsets(
		spPr: IGroupShapeProperties | IShapeProperties | IConnectionShapeProperties,
		groupSpPr: IGroupShapeProperties,
	) {
		let offX = manipulateDimensions.flipObjectPosition(spPr, groupSpPr);
		let offY = spPr.xfrm.off.y;

		let rot = spPr.xfrm.rot;
		rot = rot || 0;

		const cx = groupSpPr.xfrm.ext.cx / 2;
		const cy = groupSpPr.xfrm.ext.cy / 2;

		const objCx = spPr.xfrm.ext.cx / 2;
		const objCy = spPr.xfrm.ext.cy / 2;

		const groupRot = groupSpPr.xfrm.rot;

		rot += groupRot;

		// Calculate the position of the shape post-rotation about the center of the group
		const pointsObj = manipulateDimensions.rotatedPoint(cx, cy, offX, offY, -groupRot);

		// Account for the rotational adjustment of the object's top left
		const newTopLeft = manipulateDimensions.rotatedPoint(objCx, objCy, 0, 0, -groupRot);

		// Absolute offset is group offset + relative offset of object within group + adjustment for
		// object's effective top left based on its rotation
		offX = groupSpPr.xfrm.off.x + pointsObj.nx - newTopLeft.nx;
		offY = groupSpPr.xfrm.off.y + pointsObj.ny - newTopLeft.ny;

		return { offX, offY, rot };
	},

	/**
	 * Given an object and the group it's in, if the group is flipped returns the proper x-offset for the object
	 * @param spPr shape properties of the object
	 * @param groupSpPr shape properties of the group
	 * @returns Number x-offset that should be used in calculations for the object
	 */
	flipObjectPosition: (
		spPr: IGroupShapeProperties | IShapeProperties | IConnectionShapeProperties,
		groupSpPr: IGroupShapeProperties,
	) => {
		if (groupSpPr.xfrm.flipH) {
			return groupSpPr.xfrm.ext.cx - spPr.xfrm.off.x - spPr.xfrm.ext.cx;
		} else {
			return spPr.xfrm.off.x;
		}
	},

	/**
	 * Given the set of page objects, find out points such that minimum offset and maximum extents are formed.
	 * @param pageObjects Array of page objects
	 * @returns Object containing minimum offset and maximum extents manipulated against given set of page objects.
	 */
	forMinOffsetsMaxExtents: (pageObjects: PageObjectType[]): { off: IOffset; ext: IExtents } => {
		let points: Array<{ x: number; y: number }> = [];
		pageObjects.map((value) => {
			points = points.concat(getDimensions(value));
		});
		// Find the point which has minimum x coordinate
		const offX = _.minBy(points, (p) => p.x);
		// Find the point which has minimum y coordinate
		const offY = _.minBy(points, (p) => p.y);
		// Find the point which has maximum x coordinate
		const extCx = _.maxBy(points, (p) => p.x);
		// Find the point which has maximum y coordinate
		const extCy = _.maxBy(points, (p) => p.y);

		const off: IOffset = {
			x: offX ? offX.x : 0,
			y: offY ? offY.y : 0,
		};
		const ext: IExtents = {
			cx: extCx ? extCx.x - off.x : 0,
			cy: extCy ? extCy.y - off.y : 0,
		};

		return { off, ext };
	},

	/**
	 * Get delta between current pointer position and point which is being dragged
	 * @param po the PageObject
	 * @param scalePointType Scale point type
	 * @param currentPosition current pointer position
	 * @returns { IPosition } delta
	 */
	getDeltaWithCurrentPosition: (
		po: PageObjectType, scalePointType: PageObjectScalePointType,
		currentPosition: IPosition,
	): IPosition => {
		const newDelta = { x: 0, y: 0 };
		const dimensions = getDimensions(po);

		switch (scalePointType) {
			case PageObjectScalePointType.CORNER_RIGHT_BOTTOM: // right, bottom corner
				newDelta.x = currentPosition.x - dimensions[1].x;
				newDelta.y = currentPosition.y - dimensions[1].y;
				break;
			case PageObjectScalePointType.CORNER_LEFT_BOTTOM: // left, bottom corner
				newDelta.x = currentPosition.x - dimensions[2].x;
				newDelta.y = currentPosition.y - dimensions[2].y;
				break;
			case PageObjectScalePointType.CORNER_RIGHT_TOP: // right,top corner
				newDelta.x = currentPosition.x - dimensions[0].x;
				newDelta.y = currentPosition.y - dimensions[0].y;
				break;
			case PageObjectScalePointType.CORNER_LEFT_TOP: // left,top corner
				newDelta.x = currentPosition.x - dimensions[3].x;
				newDelta.y = currentPosition.y - dimensions[3].y;
				break;
			default:
				break;
		}
		return newDelta;
	},

	/**
	 * Get group shape child offsets, post ungroup.
	 * The matrix possesses rotation and flip information, if any. So the new offsets for child shapes are with due
	 * consideration of these.
	 * @param matrix Group shape matrix
	 * @param child Group shape child
	 * @param gs Group shape
	 */
	getOffsetsPostUngroup: (child: PageObjectType, gs: IGroupShape): IOffset => {
		const newOff = _.cloneDeep(child.spPr.xfrm.off);
		const matrix = getMatrixForObject(gs);
		if (matrix) {
			const { x, y } = child.spPr.xfrm.off;
			const identityMatrix = new DOMMatrix();

			// Compute group shape center.
			// When we have group shape rotation, the rotation is with respect to center. So this point is common in
			// both rotated and non rotated group shape coordinate system.
			const gsCenter = {
				x: gs.spPr.xfrm.off.x + (gs.spPr.xfrm.ext.cx / 2),
				y: gs.spPr.xfrm.off.y + (gs.spPr.xfrm.ext.cy / 2),
			};

			// Compute child shape center.
			// Like group shape, shapes too are rotated with respect to center.
			// So we will find new center position post ungroup.
			const shapeCenter = {
				x: gs.spPr.xfrm.off.x + x + child.spPr.xfrm.ext.cx / 2,
				y: gs.spPr.xfrm.off.y + y + child.spPr.xfrm.ext.cy / 2,
			};

			// Translate group shape matrix to group center
			const gsMatrix = identityMatrix.translate(gsCenter.x, gsCenter.y)
				.multiply(matrix);

			// Get matrix for shape with translation to shape center
			let shapeMatrix = identityMatrix.translate(shapeCenter.x - gsCenter.x, shapeCenter.y - gsCenter.y, 0);

			// Apply group shape matrix to shape matrix to get new shape center position
			shapeMatrix = gsMatrix.multiply(shapeMatrix);

			// Compute offsets, given projected new center and shape dimensions
			newOff.x = shapeMatrix.e - child.spPr.xfrm.ext.cx / 2;
			newOff.y = shapeMatrix.f - child.spPr.xfrm.ext.cy / 2;
		}
		return newOff;
	},

	/**
	 * Resize object based on scale handler position.
	 * @param po the PageObject
	 * @param scalePointType Scale point type. Required to deduce object resize strategy
	 * @param isMaintainAspectRatio Flag to indicate if we need to maintain aspect ratio.
	 * @param bCenter Flag to indicate if resize is happening with respect to shape centre
	 * @param delta Delta with respect to x and y axis
	 * @param aspectRatio Aspect ratio of shape width and height
	 * @param isGhostImageScale Flag to indicate if scaling operation is under picture edit
	 *  and scaling is being performed for image being cropped
	 * @param refScale In case of multiple shapes, it gives resize meta data for reference shape.
	 */
	resizeObject: (
		po: PageObjectType, scalePointType: PageObjectScalePointType, isMaintainAspectRatio: boolean,
		bCenter: boolean, delta: IPosition, aspectRatio: number, isGhostImageScale: boolean,
		refScale: IResizeMeta | null, groupObject?: IGroupShape | undefined,
	)
		: IResizeMeta => {
		let resizeMeta = refScale;

		if (!resizeMeta) {
			resizeMeta = _scaleObject(
				po,
				scalePointType, delta, bCenter,
				isGhostImageScale, true, isGhostImageScale ? aspectRatio : 0, groupObject,
			);
		}
		let newDelta = delta;
		const isCorner = isCornerScalePoint(scalePointType);

		if (isMaintainAspectRatio && isCorner) {
			let ext = po.spPr.xfrm.ext;

			// use image extents for ghost shape scale
			if (isGhostImageScale) {
				ext = getImageExtents(po as IShape);
				aspectRatio = ext.cx / ext.cy;
			}
			if (aspectRatio === undefined || aspectRatio === 0) {
				aspectRatio = ext.cx / ext.cy;
			}
			newDelta = manipulateDimensions.applyAspectRatio(
				po.spPr.xfrm.ext,
				ext,
				scalePointType,
				resizeMeta.dWidth,
				resizeMeta.dHeight,
				aspectRatio,
			);
			resizeMeta = _scaleObject(po, scalePointType, newDelta, bCenter, false, false, aspectRatio, groupObject);
		}
		return resizeMeta;
	},

	/**
	 * Find the new position of a point after rotating it about another given point
	 */
	rotatedPoint: (cx: number, cy: number, x: number, y: number, angle: number) => {
		const radians = (Math.PI / 180) * angle;
		const cos = Math.cos(radians);
		const sin = Math.sin(radians);
		const nx = (cos * (x - cx)) + (sin * (y - cy)) + cx;
		const ny = (cos * (y - cy)) - (sin * (x - cx)) + cy;

		return { nx, ny };
	},
};

/**
 * Convert a group's objects to use the new group extents
 * @param group group whose objects should be converted
 */
export const adjustGroupExtents = (group: IGroupShape, off: any, ext: any) => {
	const radians = degToRad(group.spPr.xfrm.rot);

	// Calculate change in offset
	const offDiffX = -group.spPr.xfrm.off.x + off.x;
	const offDiffY = -group.spPr.xfrm.off.y + off.y;

	// Calculate how much bottom and right side of (unrotated) group move.
	// Add offsets to get only the part of extent increase that is bottom/right
	let rightDiff = -group.spPr.xfrm.ext.cx + ext.cx + offDiffX;
	let botDiff = -group.spPr.xfrm.ext.cy + ext.cy + offDiffY;

	// We want to rotate and keep same direction as the offset uses
	rightDiff *= -1;
	botDiff *= -1;

	// Internally, adjust the group objects to keep them in the same relative position.
	// This is separate from the rest of the calculations and serves to make sure the
	// inside of the group remains consistent
	_.each(group.childObjects, (object) => {
		let flipDiffX = offDiffX;

		if (group.spPr.xfrm.flipH) {
			flipDiffX = rightDiff;
		}

		object.spPr.xfrm.off.x = object.spPr.xfrm.off.x - flipDiffX;
		object.spPr.xfrm.off.y = object.spPr.xfrm.off.y - offDiffY;
	});

	let translateX = 0;
	let translateY = 0;

	// For change in offset, we want to only use the part extending to the left and
	// top of the shape, since those affect the offset.
	const posPart = (num: number) => {
		if (num > 0) {
			return num;
		} else {
			return 0;
		}
	};

	// Rotate the motion of the side by the appropriate amount, then add it to the
	// translation.  Each side will be calculated separately.
	const rotateAndAdd = (num: number, rot: number) => {
		if (num === 0) {
			return;
		}

		const useRadians = radians + degToRad(rot);

		const xVal = posPart(Math.cos(useRadians)) * num;
		const yVal = posPart(Math.sin(useRadians)) * num;

		translateX += xVal;
		translateY += yVal;
	};

	// Calculate each side individually now, and apply their effect to the main group
	rotateAndAdd(offDiffX, 0);
	rotateAndAdd(offDiffY, 90);
	rotateAndAdd(rightDiff, 180);
	rotateAndAdd(botDiff, 270);

	// Calculate change in group position based on change in size of rotated shape
	const oldRotatedOff = manipulateDimensions.forMinOffsetsMaxExtents([group]).off;
	group.spPr.xfrm.ext = ext;
	const newRotatedOff = manipulateDimensions.forMinOffsetsMaxExtents([group]).off;

	translateX += oldRotatedOff.x - newRotatedOff.x;
	translateY += oldRotatedOff.y - newRotatedOff.y;

	// Add final translation calculation to the shape
	group.spPr.xfrm.off.x += translateX;
	group.spPr.xfrm.off.y += translateY;
};

/**
 * Calculate and adjust group extents
 * @param group - group to calculate and adjust extents
 */
export const calculateAdjustGroup = (group: IGroupShape): void => {
	// Reset the group rotation temporarily so that we can calculate size of the group without it,
	// then adjust for rotation later
	const { xfrm } = group.spPr;
	const oldRot = xfrm.rot;
	xfrm.rot = 0;
	const { off, ext } = manipulateDimensions.calculateGroupExtents(group);
	xfrm.rot = oldRot;
	// Only need to adjust the extents of the group if its position or size has changed
	if (xfrm.off !== off || xfrm.ext !== ext) {
		adjustGroupExtents(group, off, ext);
	}
};

/**
 * Determine delta to maintain width/height aspect ratio
 * @param  {IPosition} delta
 * @param  {number} aspectRatio
 * @param  {PageObjectScalePointType} scalePoint
 * @returns {IPosition}
 */
export const getDeltaWithAspectRatio = (
	delta: IPosition, aspectRatio: number,
	scalePoint: PageObjectScalePointType,
): IPosition => {
	if (aspectRatio) {
		switch (scalePoint) {
			case PageObjectScalePointType.CORNER_RIGHT_TOP:
			case PageObjectScalePointType.CORNER_LEFT_BOTTOM:
				// negating value of delta here, as we are using delta.x value to calculate the delta.y and delta.y
				// value to calculate delta.x
				if (Math.abs(delta.x) > Math.abs(delta.y)) {
					delta.y = (delta.x / aspectRatio) * -1;
				} else {
					delta.x = (delta.y * aspectRatio) * -1;
				}
				break;
			case PageObjectScalePointType.CORNER_LEFT_TOP:
			case PageObjectScalePointType.CORNER_RIGHT_BOTTOM:
				if (delta.x > delta.y) {
					delta.y = (delta.x / aspectRatio);
				} else {
					delta.x = (delta.y * aspectRatio);
				}
				break;
			default:
				break;
		}
	}
	return delta;
};

/**
 * Radian between two points.
 * @param p1 offset.
 * @param p2 offset.
 */
export const radian = (p1: IOffset, p2: IOffset) => {
	let r = Math.atan2(p2.y - p1.y, p2.x - p1.x);
	r = (r < 0) ? 2 * Math.PI + r : r;
	return (r - (1.5 * Math.PI));
};

/**
 * Convert radians to degrees
 * @param r { number }
 */
export const radToDeg = (r: number) => {
	return r * 180 / Math.PI;
};

/**
 * Convert degrees to radians
 * @param d { number }
 */
export const degToRad = (d: number) => {
	return d * Math.PI / 180;
};

/**
 * Truncate to given precision
 * @param value { number }
 * @param precision { number }
 */
export const truncate = (value: number, precision: number) => {
	const s = Math.pow(10, precision);
	return Math.round(Number(value) * s) / s;
};

/**
 * Filter Un locked page objects.
 * @param pageObjects Array of unlocked Page Objects.
 */
export const filterUnLockedObjects = (value: PageObjectType, index: number, array: PageObjectType[]) => {
	return !value.spPr.locked;
};

/**
 * Filter selected objects function.
 * @param {number[]} Array of selected objects indexes.
 */
export const filterSelectedObjects = (selectedObjectIndexes: number[]) =>
	(value: PageObjectType, index: number, array: PageObjectType[]) => {
		return selectedObjectIndexes.indexOf(index) >= 0;
	};

export const getSelectedUnlockedObjects = (page: IPage, action: any) => {
	const selectedObjects = page.pageObjects.filter(filterSelectedObjects(action.selectedPageObjects));
	let unlockedSelectedObjects = selectedObjects.filter(filterUnLockedObjects);
	if (unlockedSelectedObjects.length === 1 && action.groupSelectionIndices &&
		action.groupSelectionIndices.length > 0) {
		const groupObject = unlockedSelectedObjects[0] as IGroupShape;
		unlockedSelectedObjects = getIndexedChildren(groupObject.childObjects, action.groupSelectionIndices);
	}

	return unlockedSelectedObjects;
};

export const getOuterGroup = (page: IPage, action: any) => {
	let selectedObjects;
	// If an ID is provided for a group we are going to replace, use it instead.
	if (action.objectId) {
		selectedObjects = deepFindObjectById(page.pageObjects, action.objectId);
		return selectedObjects.pageObjects[selectedObjects.x];
	} else {
		selectedObjects = page.pageObjects.filter(filterSelectedObjects(action.selectedPageObjects));
		const unlockedSelectedObjects = selectedObjects.filter(filterUnLockedObjects);
		return unlockedSelectedObjects[0];
	}
};

/**
 * Return object box.
 * @param spPr shape properties.
 */
export const getObjectBox = (spPr: IPageObjectProperties<ITransform2D>): IRectBox => {
	return {
		bottom: spPr.xfrm.off.y + spPr.xfrm.ext.cy,
		left: spPr.xfrm.off.x,
		right: spPr.xfrm.off.x + spPr.xfrm.ext.cx,
		top: spPr.xfrm.off.y,
	};
};

/**
 * Return object center.
 * @param spPr shape properties.
 * @param groupSpPr properties of the group, if defined we will find the absolute page center of the object
 */
export const getObjectCenter = (
	spPr: IPageObjectProperties<ITransform2D>,
	groupSpPr?: IPageObjectProperties<ITransform2D>,
) => {
	const prop = spPr as IShapeProperties;
	const gSpPr = groupSpPr as IGroupShapeProperties;

	// We always include the object's size in its center
	let cx = spPr.xfrm.ext.cx / 2;
	let cy = spPr.xfrm.ext.cy / 2;

	if (groupSpPr) {
		// If calculating absolute offset based on group, call helper method
		const { offX, offY } = manipulateDimensions.calculateSubgroupAbsoluteOffsets(prop, gSpPr);

		cx += offX;
		cy += offY;
	} else {
		// Otherwise we can just use the object's position
		cx += spPr.xfrm.off.x;
		cy += spPr.xfrm.off.y;
	}

	return {
		x: cx,
		y: cy,
	};
};

/**
 * Calculate guides points to draw vertical/horizontal lines
 * @param state { ICanvasEditorState } Canvas editor state
 * @param selectedObject { PageObjectType } selected page object
 * @param unselectedObjects { PageObjectType[] } unselected Objects
 * @param dx { number }
 * @param dy { number }
 * @return guides
 */
export const getGuidesPoints = (
	state: ICanvasEditorState, selectedObject: PageObjectType,
	unselectedObjects: PageObjectType[], dx: number, dy: number,
) => {
	const guides: any = {};
	const groupSelectionIndices = state.selection.present.groupSelectionIndices;
	const groupObject = selectedObject as any as IGroupShape;
	const subgroupSelection = groupSelectionIndices !== undefined && groupSelectionIndices.length > 0;
	if (selectedObject) {
		if (subgroupSelection && groupObject.childObjects && groupSelectionIndices) {
			selectedObject = _.cloneDeep(groupObject.childObjects[groupSelectionIndices[0]]);

			const spPr = selectedObject.spPr;

			const resultObj = manipulateDimensions.calculateSubgroupAbsoluteOffsets(spPr, groupObject.spPr);

			spPr.xfrm.off.x = resultObj.offX;
			spPr.xfrm.off.y = resultObj.offY;
			spPr.xfrm.rot = resultObj.rot;
		}

		const objectCenter = getObjectCenter(selectedObject.spPr);
		const objectCenterX = objectCenter.x + dx;
		const objectCenterY = objectCenter.y + dy;
		/**
		 * Guides for editor canvas center
		 */
		// Canvas center vertical and horizontal line
		if (Math.abs(state.canvasEditor.editorHeight / 2 - objectCenterY) <= GUIDE_SNAP_DIFF) {
			// horizontal guide line
			guides.y = {
				x1: 0,
				x2: state.canvasEditor.editorWidth,
				y1: state.canvasEditor.editorHeight / 2,
				y2: state.canvasEditor.editorHeight / 2,
			};
			guides.dy = (guides.y.y1 - objectCenter.y);
		}
		if (Math.abs(state.canvasEditor.editorWidth / 2 - objectCenterX) <= GUIDE_SNAP_DIFF) {
			// vertical guide line
			guides.x = {
				x1: state.canvasEditor.editorWidth / 2,
				x2: state.canvasEditor.editorWidth / 2,
				y1: 0,
				y2: state.canvasEditor.editorHeight,
			};
			guides.dx = (guides.x.x1 - objectCenter.x);
		}
	}

	/**
	 * Shapes guides
	 */
	// Array of functions which contains object (x+ width/2, x, x + width)
	const objectsXpoints = [
		(dimensions: IPosition[], spPr: IPageObjectProperties<ITransform2D>) => {
			return Math.round(spPr.xfrm.off.x + (spPr.xfrm.ext.cx / 2));
		},
		(dimensions: IPosition[], spPr: IPageObjectProperties<ITransform2D>) => {
			const minX = _.minBy(dimensions, (p: IPosition) => p.x) as IPosition;
			return Math.round(minX && minX.x);
		},
		(dimensions: IPosition[], spPr: IPageObjectProperties<ITransform2D>) => {
			const maxX = _.maxBy(dimensions, (p: IPosition) => p.x) as IPosition;
			return Math.round(maxX && maxX.x);
		},
	];
	// Array of functions which contains shapes (y+ height/2, y, y + height)
	const objectsYpoints = [
		(dimensions: IPosition[], spPr: IPageObjectProperties<ITransform2D>) => {
			return Math.round(spPr.xfrm.off.y + (spPr.xfrm.ext.cy / 2));
		},
		(dimensions: IPosition[], spPr: IPageObjectProperties<ITransform2D>) => {
			const minY = _.minBy(dimensions, (p: IPosition) => p.y) as IPosition;
			return Math.round(minY && minY.y);
		},
		(dimensions: IPosition[], spPr: IPageObjectProperties<ITransform2D>) => {
			const maxY = _.maxBy(dimensions, (p: IPosition) => p.y) as IPosition;
			return Math.round(maxY && maxY.y);
		},
	];
	const dragShapeDimensions: IPosition[] = getDimensions(selectedObject);
	[objectsXpoints, objectsYpoints].forEach((dragShapePoints: Function[], index: number) => {
		const isXCoordinate = !index;
		// check for all unselected object

		if (subgroupSelection && groupSelectionIndices) {
			unselectedObjects = [] as PageObjectType[];
			unselectedObjects.push(groupObject);
			_.each(groupObject.childObjects, (object, i) => {
				if (i !== groupSelectionIndices![0]) {
					object = _.cloneDeep(object);

					const spPr = object.spPr;

					const resultObj = manipulateDimensions.calculateSubgroupAbsoluteOffsets(spPr, groupObject.spPr);

					spPr.xfrm.off.x = resultObj.offX;
					spPr.xfrm.off.y = resultObj.offY;
					spPr.xfrm.rot = resultObj.rot;

					unselectedObjects.push(object);
				}
			});
		}

		_.every(unselectedObjects, (unselectedObject) => {
			const shapeDimensions: IPosition[] = getDimensions(unselectedObject);
			const matchedIndex: number[] = [];
			// Check for left, center and right match
			dragShapePoints.forEach((shapePoint: Function) => {
				// get matched points function
				const matchedPointFns = dragShapePoints.filter((dragShapePointFn: Function, dragIndex: number) => {
					const guideCondition = (Math.abs(shapePoint(shapeDimensions, unselectedObject.spPr) -
						(dragShapePointFn(dragShapeDimensions, selectedObject.spPr) +
							(isXCoordinate ? dx : dy))) <= GUIDE_SNAP_DIFF);
					if (guideCondition) {
						matchedIndex.push(dragIndex);
					}
					// matchedIndex.length === 1 will make sure only one
					// vertical or horizontal line will be draw at a time
					return guideCondition && matchedIndex.length === 1;
				});

				// break current loop if canvas Vertical or Horizontal is matched
				if ((guides.x && isXCoordinate) || (guides.y && !isXCoordinate)) {
					return false;
				}

				if (matchedPointFns.length) {
					const diff = shapePoint(shapeDimensions, unselectedObject.spPr) -
						(matchedPointFns[0](dragShapeDimensions, selectedObject.spPr));
					// Snapping shapes to guides
					if (isXCoordinate) {
						guides.x = calculateVerticalLinePoints(
							matchedPointFns[0](dragShapeDimensions, selectedObject.spPr) + diff,
							dragShapeDimensions, shapeDimensions,
						);
						guides.dx = diff;
					} else {
						guides.y = calculateHorizontalLinePoints(
							matchedPointFns[0](dragShapeDimensions, selectedObject.spPr) + diff,
							dragShapeDimensions, shapeDimensions,
						);
						guides.dy = diff;
					}
				}
			});
			// if matched index has index entry , then will not check for the other shapes for isXCoordinate
			return !matchedIndex.length;
		});
	});
	return guides;
};

/**
 * Calculate vertical line points
 * Depending on whether the dragged shape is on left or right side of unselected shapes
 * @param dragX { number }
 * @param dragShapeDimensions { IPosition[] }
 * @param shapeDimensions { IPosition[] }
 */
export const calculateVerticalLinePoints = (
	dragX: number,
	dragShapeDimensions: IPosition[], shapeDimensions: IPosition[],
) => {
	const dragMinY = _.minBy(dragShapeDimensions, (p: IPosition) => p.y) as IPosition;
	const shapeMinY = _.minBy(shapeDimensions, (p: IPosition) => p.y) as IPosition;
	const shapeMaxY = _.maxBy(shapeDimensions, (p: IPosition) => p.y) as IPosition;
	const dragMaxY = _.maxBy(dragShapeDimensions, (p: IPosition) => p.y) as IPosition;
	dragX = Math.round(dragX);
	let drawLines = {};
	if (dragMinY.y > shapeMinY.y) {
		drawLines = {
			x1: dragX,
			x2: dragX,
			y1: Math.round(dragMaxY.y),
			y2: Math.round(shapeMinY.y),
		};
	} else {
		drawLines = {
			x1: dragX,
			x2: dragX,
			y1: Math.round(dragMinY.y),
			y2: Math.round(shapeMaxY.y),
		};
	}
	return drawLines;
};

/**
 * Calculate horizontal line points
 * depending on whether the dragged shape is on above or below of unselected shapes
 * @param dragY { number }
 * @param dragShapeDimensions { IPosition[] }
 * @param shapeDimensions { IPosition[] }
 */
export const calculateHorizontalLinePoints = (
	dragY: number,
	dragShapeDimensions: IPosition[], shapeDimensions: IPosition[],
) => {
	let drawLines;
	const dragMinX = _.minBy(dragShapeDimensions, (p: IPosition) => p.x) as IPosition;
	const shapeMinX = _.minBy(shapeDimensions, (p: IPosition) => p.x) as IPosition;
	const dragMaxX = _.maxBy(dragShapeDimensions, (p: IPosition) => p.x) as IPosition;
	const shapeMaxX = _.maxBy(shapeDimensions, (p: IPosition) => p.x) as IPosition;
	dragY = Math.round(dragY);
	if (dragMinX.x > shapeMinX.x) {
		drawLines = {
			x1: Math.round(dragMaxX.x),
			x2: Math.round(shapeMinX.x),
			y1: dragY,
			y2: dragY,
		};
	} else {
		drawLines = {
			x1: Math.round(dragMinX.x),
			x2: Math.round(shapeMaxX.x),
			y1: dragY,
			y2: dragY,
		};
	}
	return drawLines;
};

/**
 * Get first character styles
 * @param {string} elString - HTML string of element
 * @param {string} placeholderTextFormat - "all", "label", or just "value"
 * @return {Object} styles
 */
export const firstCharacterStyles = (elString: string, placeholderTextFormat?: string) => {
	const styles = {};
	const elContainer = document.createElement("div");
	elContainer.innerHTML = elString; // apply string to innerHTML of element
	const walker = document.createTreeWalker(
		elContainer.querySelector("span") || elContainer,
		NodeFilter.SHOW_TEXT,
	);
	let node = walker.nextNode() || elContainer.querySelector("#" + PLACEHOLDER_LABEL_SPAN) as any;
	if (placeholderTextFormat === PlaceholderTextFormat.VALUE && !elString.includes(PLACEHOLDER_RICH_TEXT_DIV)) {
		node = elContainer.querySelector("#" + PLACEHOLDER_VALUE_SPAN);
	}
	// if there was no text node found, e.g. text is "", traverse DOM to deepest child
	while (node && node.firstChild) {
		node = node.firstChild;
	}
	if (node) {
		if (node.nodeType === Node.TEXT_NODE) {
			node = node.parentNode; // get html element above text node
		}
		while (node !== elContainer) {
			const style = TAG_STYLES[node.nodeName];
			const tagName = node.nodeName.toLowerCase();
			if (style) {
				styles[style] = tagName;
			}
			if (["em", "s", "u"].includes(tagName)) {
				styles[tagName] = true;
			}
			_.each(node.style, (style) => {
				const value = node.style[style];
				if (style === COLOR_KEY) {
					styles[style] = rgbToHex(value);
				} else {
					// C8-27497
					// for some styles, like "Times New Roman", double quotes are added because
					// of the spaces. Then, we get something like ""Times New Roman"" for the value.
					// Remove the double quotes on the edges using the first technique in the following URL:
					// https://stackoverflow.com/questions/19156148/i-want-to-remove-double-quotes-from-a-string
					styles[style] = value.replace(/^"(.+(?="$))"$/, "$1");
				}
			});
			node = node.parentNode;
		}
		// if font size is not set, and we have a heading, set by heading or default to 12px
		if (!styles[FONTSIZE_KEY]) {
			styles[FONTSIZE_KEY] = HEADING_TAG_FONT_MAP[styles[PARAGRAPH_KEY]] || "12px";
		}
	}
	return styles;
};

/**
 * Convert RGB to HEX
 * @param rgb { any }
 */
export const rgbToHex = (rgb: any) => {
	function hex(x: string) {
		return ("0" + parseInt(x, 10)
			.toString(16)).slice(-2);
	}
	try {
		if (!rgb || rgb === "transparent") {
			return "";
		}

		if (/^#[0-9A-F]{6}$/i.test(rgb)) {
			return rgb;
		}
		rgb = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
		return ("#" + hex(rgb[1]) + hex(rgb[2]) + hex(rgb[3])).toUpperCase();
	} catch (ex) {
		return null;
	}
};

/**
 * Generate universally unique identifier
 * Reference: https://en.wikipedia.org/wiki/Universally_unique_identifier
 */
export const generateUUID = () => {
	let d = new Date()
		.getTime();
	const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		// tslint:disable-next-line:no-bitwise
		const r = (d + Math.random() * 16) % 16 | 0;
		d = Math.floor(d / 16);
		// tslint:disable-next-line:no-bitwise
		return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
	});
	return uuid;
};

/**
 * Returns array of object indices whose intersect the drag selection box
 * @param pageObjects {PageObjectType[]}
 * @param dragSelectionBox {SVGRect}
 * @return {number[]}
 */
export const pageObjectsInBox = (pageObjects: PageObjectType[], dragSelectionBox: IDragBoxProperties): number[] => {
	const newSelection: number[] = [];
	const top = dragSelectionBox.y;
	const right = dragSelectionBox.x + dragSelectionBox.width;
	const bottom = dragSelectionBox.y + dragSelectionBox.height;
	const left = dragSelectionBox.x;
	_.each(pageObjects, (pageObject, index) => {
		const box = getObjectBox(pageObject.spPr);
		if (
			left <= box.right &&
			box.left <= right &&
			top <= box.bottom &&
			box.top <= bottom
		) {
			newSelection.push(index);
		}
	});
	return newSelection;
};

/**
 * Removes placeholders with the given attribute keys from the given page model
 * @param pageObjects {PageObjectType[]}
 * @param keys {string[]}
 * @return {string[]}
 */
export const removePlaceholdersMatchingKeys = (pageObjects: PageObjectType[], keys: string[]) => {
	const resultObjects = _.filter(pageObjects, (pageObject) => {
		const shape = pageObject as IShape;
		if (shape.attributeKey && _.indexOf(keys, getStartTypeForKey(shape.attributeKey)) !== -1) {
			return false;
		}
		const po = pageObject as IGroupShape;
		if (po.childObjects) {
			const containsKey = _.some(po.childObjects, (co) => {
				const so = co as IShape;
				if (so.attributeKey) {
					return _.indexOf(keys, getStartTypeForKey(so.attributeKey)) !== -1;
				} else {
					return false;
				}
			});
			return !containsKey;
		} else {
			return true;
		}
	});

	_.each(resultObjects, (pageObject) => {
		const po = pageObject as IGroupShape;
		if (po.childObjects) {
			po.childObjects = removePlaceholdersMatchingKeys(po.childObjects, keys);
		}
	});

	return resultObjects;
};

/**
 * Unlink all placeholders from the model
 * @param pageObjects {PageObjectType[]}
 * @return {PageObjectType[]} list of page objects with all placeholders removed
 */
export const unlinkAllPlaceholders = (pageObjects: PageObjectType[]): PageObjectType[] => {
	const newObjects: PageObjectType[] = _.cloneDeep(pageObjects);
	_.each(newObjects, (pageObject) => {
		const go = pageObject as IGroupShape;
		if (go.childObjects) {
			go.isAttributePlaceholder = false;
			go.childObjects = unlinkAllPlaceholders(go.childObjects);
		} else {
			const po = pageObject as IShape;
			po.attributeKey = undefined;
			po.filled = undefined;
			po.nodeURL = undefined;
			po.showLabel = undefined;
		}
	});
	return newObjects;
};

/**
 * Round number to four decimal places by default
 * @param {number} value-number to be rounded up.
 * @param {number} [4] optional precision digit.
 * @return {number}
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round#A_better_solution
 */
export const roundNumber = (value: number, precision?: number): number => {
	precision = precision || 4;
	// tslint:disable-next-line:no-shadowed-variable
	const shift = (value: number, precision: number, reverseShift: boolean) => {
		if (reverseShift) {
			precision = -precision;
		}
		const numArray = ("" + value).split("e");
		return +(numArray[0] + "e" + (numArray[1] ? (+numArray[1] + precision) : precision));
	};
	return shift(Math.round(shift(value, precision, false)), precision, true);
};

/**
 * Given a page object, get it's center in terms of points.
 * @param po {PageObjectType}
 * @returns {x: number, y: number}
 */
export const getShapeCenter = (po: PageObjectType) => {
	const { x, y } = po.spPr.xfrm.off;
	const { cx, cy } = po.spPr.xfrm.ext;

	// Center of page object
	const center = { x: x + (cx / 2), y: y + (cy / 2) };

	return center;
};

/**
 * Adds an attribute to the object
 * @param pageObject placeholder to add attribute to
 * @param attribute attribute to be added
 */
export const addAttributeToObject = (pageObject: IShape, attribute: IPlaceholderFillData) => {
	const {
		format,
		permission,
		text,
	} = attribute;

	// create JS object representation of the content element
	const pageObjectElement = document.createElement("div");
	pageObjectElement.innerHTML = pageObject.txBody.content;

	// get value content element
	const valueSpanNode: HTMLElement = pageObjectElement.querySelector(`#${PLACEHOLDER_VALUE_SPAN}`);

	let richTextEl;

	// get the text node of the value element using the TreeWalker API
	const walker = document.createTreeWalker(valueSpanNode, NodeFilter.SHOW_TEXT);
	let textNode = walker.nextNode();

	if (format === types.FORMAT_RICH) {
		// blank value span
		valueSpanNode.innerHTML = "";

		// sanitize the rich text
		const sanitizedRichText = html.sanitize(text);

		// add or replace rich text div
		richTextEl = getPlaceholderRichTextElement(pageObjectElement);
		if (richTextEl) {
			richTextEl.innerHTML = sanitizedRichText;
		} else {
			const div = document.createElement("div");
			div.id = PLACEHOLDER_RICH_TEXT_DIV;
			div.innerHTML = sanitizedRichText;
			pageObjectElement.append(div);
		}
	} else if (format === types.FORMAT_TEXTAREA) {
		valueSpanNode.innerHTML = "";
		text.split("\n")
			.forEach((lineOfText, i) => {
				valueSpanNode.appendChild(document.createElement("br"));
				valueSpanNode.appendChild(document.createTextNode(lineOfText));
			});
	} else {
		// if there isn't a text node, insert one with no value
		if (!textNode) {
			textNode = document.createTextNode("");
			valueSpanNode.appendChild(textNode);
		}

		// set value element text without changing style
		textNode.nodeValue = text;
	}

	// placeholder is now filled
	pageObject.filled = true;

	// set permission flag
	pageObject.noPermissionToFill = !permission;

	// if we don't have permission to fill, turn the value element red
	if (!permission) {
		(valueSpanNode as HTMLElement).style.color = "var(--Red700)";
	}

	// if the labels were previously turned off, all of them will be displaying the disabled span
	// we need to turn them off based on whether or not there is a value in the placeholder ("" not allowed)
	const disabledSpan = pageObjectElement.querySelector(`#${PLACEHOLDER_DISABLED_SPAN}`);
	const hideLabel = text.length || richTextEl?.firstChild;
	if (hideLabel) {
		html.setVisible(disabledSpan, false);
	}

	// replace content
	pageObject.txBody.content = pageObjectElement.innerHTML;
};

/**
 * Restore non-populated image placeholder display
 * @param object placeholder to restore
 * @param saveURL flag to save the node URL
 */
export const restoreDefaultPlaceholderView = (object: IShape, saveURL: boolean) => {
	const nonText = object.isPicture || object.isPictureFromPlaceholder || object.imagePlaceholder;
	const displayName = getDisplayNameForKey(object.attributeKey);
	// Restore properties.
	if (nonText) {
		delete object.isPicture;
		delete object.isPictureFromPlaceholder;
		object.imagePlaceholder = true;
		object.drawDimensions = true;
		_.assign(object.spPr, DEFAULT_PLACEHOLDER_SHAPE_PROPERTIES.spPr);
		// Restore text body
		object.txBody = {
			content: "<p style=\"text-align: center;\"><span style=\"font-family:" +
				`${DEFAULT_FONT_FAMILY}; font-size: ${DEFAULT_FONT_SIZE}px; color: ${DEFAULT_COLOR};\">` +
				`${displayName}</span></p>`,
		};
	} else {
		object.allowsArrows = false;
		object.allowsFill = true;
		object.noChangeAspect = false;
		// Restore text body
		object.txBody = getDefaultTextPlaceholderHTML(displayName);
	}

	if (!saveURL) {
		delete object.nodeURL;
		object.filled = false;
	}
};

/**
 * Moves objects optionally to the front or back of an array
 * @param pageObjects array of objects
 * @param selectedPageObjects objects selected in the array
 * @param bFront whether to send the objects to the front
 */
export const moveObjectsToFront = (
	pageObjects: PageObjectType[],
	selectedPageObjects: number[], bFront: boolean,
): PageObjectType[] => {
	const pageObjReference: any = {};

	// Keep selected objects reference with current indices
	selectedPageObjects.forEach((index) => {
		pageObjReference[index] = pageObjects[index];
	});

	// Remove selected objects from pageObjects
	pageObjects = pageObjects.filter((pageObject, index) => {
		return selectedPageObjects.indexOf(index) === -1;
	});

	// Sort selected objects indices depending on we want to add it at front or back
	selectedPageObjects.sort((a, b) => {
		const result = !bFront ? (b - a) : (a - b);
		return result;
	});

	// Push selected objects into the pageObjects
	selectedPageObjects.forEach((selectedObjectIndex, index) => {
		if (bFront) {
			pageObjects.push(pageObjReference[selectedObjectIndex]);
		} else {
			pageObjects.unshift(pageObjReference[selectedObjectIndex]);
		}
	});

	return pageObjects;
};

/**
 * Get page object representation for the given image.
 * @param x Image x offset position
 * @param y Image y offset position
 * @param width Image width
 * @param height Image height
 * @param imageLink Image url
 * @returns {IPageObject} image object.
 */
export const getImageObject = (image: IAddImageRequest) => {
	return {
		isPicture: true,
		noChangeAspect: false,
		shapeId: generateUUID(),
		spPr: {
			fill: {
				blip: {
					link: image.imageLink,
				},
				srcRect: {
					b: 0,
					flipH: false,
					l: 0,
					r: 0,
					rot: 0,
					t: 0,
				},
				type: FillTypes.PICTURE_FILL,
			},
			geometry: {
				presetShape: ShapeType.rect,
				type: GeometryTypes.PRESET,
			},
			ln: {
				W: 0,
				fill: {
					color: "none",
					type: FillTypes.SOLID_FILL,
				},
			},
			xfrm: {
				ext: { cx: image.width, cy: image.height },
				flipH: false,
				flipV: false,
				off: {
					x: image.x,
					y: image.y,
				},
				rot: 0,
			},
		},
		type: IPageObjectTypes.SHAPE,
	} as IShape;
};

/**
 * Image Placeholder border is always black.
 * Get page object representation for an attribute image placeholder.
 * @param image {Object} contains image attribute key, height, width, x, y
 * @returns {IPageObject} image object.
 */
export const getImagePlaceholderObject = (image: {
	attributeKey: string;
	height: number;
	isDefaultImage?: boolean;
	type: string;
	width: number;
	x: number;
	y: number;
}) => {
	// Display placeholder type for default images,
	// otherwise get display name for labelled images.
	return {
		allowsArrows: false,
		allowsFill: true,
		attributeKey: image.attributeKey,
		drawDimensions: true,
		imagePlaceholder: true,
		noChangeAspect: false,
		shapeId: generateUUID(),
		spPr: {
			fill: {
				color: IMAGE_PLACEHOLDER_FILL_COLOR,
				type: FillTypes.SOLID_FILL,
			},
			geometry: {
				presetShape: ShapeType.rect,
				type: GeometryTypes.PRESET,
			},
			ln: {
				W: 2,
				fill: {
					color: DEFAULT_COLOR,
				},
			},
			xfrm: {
				ext: {
					cx: image.width,
					cy: image.height,
				},
				flipH: false,
				flipV: false,
				off: {
					x: image.x,
					y: image.y,
				},
				rot: 0,
			},
		},
		txBody: {
			content: "<p style=\"text-align: center;\"><span style=\"font-family: " +
				`${DEFAULT_FONT_FAMILY}; font-size: ${DEFAULT_FONT_SIZE}px; color: ${DEFAULT_COLOR};\">` +
				`${getDisplayNameForKey(image.attributeKey)}</span></p>`,
		},
		type: IPageObjectTypes.SHAPE,
	} as IShape;
};

/**
 * Get page object representation for an text attribute placeholder.
 * @param attribute {Object} contains attribute key, height, width, x, y
 * @returns {IPageObject} attribute object.
 */
export const getAttributePlaceholderObject = (attribute: {
	attributeKey: string;
	height: number;
	width: number;
	x: number;
	y: number;
}) => {
	// Display display name for attributes.
	const displayName = getDisplayNameForKey(attribute.attributeKey);
	return {
		allowsArrows: false,
		allowsFill: true,
		attributeKey: attribute.attributeKey,
		isTableShape: false,
		isTextShape: true,
		noChangeAspect: false,
		shapeId: generateUUID(),
		showLabel: true,
		spPr: {
			autosize: AutosizeMode.FIXED_WIDTH,
			fill: {
				color: "none",
				type: "SOLID_FILL",
			},
			geometry: {
				presetShape: "rect",
				type: "PRESET",
			},
			ln: {
				fill: {
					type: "NO_FILL",
				},
			},
			verticalAlign: "center",
			xfrm: {
				ext: {
					cx: attribute.width,
					cy: attribute.height,
				},
				flipH: false,
				flipV: false,
				off: {
					x: attribute.x,
					y: attribute.y,
				},
				rot: 0,
			},
		},
		txBody: getDefaultTextPlaceholderHTML(displayName),
		type: "SHAPE",
	} as IShape;
};

/**
 * Private helper function to get the left margin of an HTML element
 * @param {HTMLElement} element - element to grab margin left from
 * @return {number} - return the margin in the form of a float with scale factor
 */
const _getMarginLeft = (element: HTMLElement) => {
	const { offsetWidth } = element;
	if (offsetWidth) {
		const scale = element.getBoundingClientRect().width / offsetWidth;
		return parseFloat(element.style.marginLeft || ZERO_TEXT) * scale;
	}
	return 0;
};

/**
 * Get the Froala Editor width by iterating through each text node of editor text.
 * We'll use the TreeWalker API with a range to walk through each node grabbing the position.
 * We'll store the top and left positions first, then get the bottom and right positions on each iteration.
 * The maximum distance between top, bottom, left and right will get us our width and height.
 */
export const getTextDimensions = (element: HTMLElement) => {
	const children = element.children;
	const range = document.createRange();
	const baseRect = children[0].getBoundingClientRect();
	const left = baseRect.left;
	const top = baseRect.top;
	let [height, width, marginLeft] = [0, 0, _getMarginLeft(children[0] as HTMLElement)];
	for (const child of children) {
		const walker = document.createTreeWalker(child, NodeFilter.SHOW_TEXT);
		let node = walker.nextNode();
		while (node) {
			range.selectNodeContents(node);
			const nodeRect = range.getBoundingClientRect();
			height = Math.max(height, nodeRect.bottom - top);
			width = Math.max(width, nodeRect.right - left);
			marginLeft = Math.min(marginLeft, _getMarginLeft(child as HTMLElement));
			width = Math.max(width, (nodeRect.right - left), (nodeRect.right - (left - marginLeft)));
			node = walker.nextNode();
		}
	};

	return { height, width };
};

/**
 * Get an active Froala link popup
 */
export const getFroalaLinkPopupElement = (): HTMLElement | undefined => {
	return document.body.querySelector(".fr-popup.fr-active");
};

/**
 * Change style of selection in shape(s)
 * There are 3 cases to consider when changing the style of selection in canvas:
 * 1. Selection Mode where the shape is selected
 * 2. Edit Mode where the shape with the current editor is selected
 * 3. Edit Mode where the selection is not within the shape that contains the editor
 * Only option 2 and 3 are handled here, as there is no Froala Editor Instance in option 1
 * When dealing with option 3, some commands (apply) will need the function "_forceSelectionToEditor"
 * because Froala does not default selection back to the current editor for these commands
 * @param editor {any} current Froala editor
 * @param style {string} type of style to pass to selection
 * @param value {string} value of style to pass to selection
 * @param {boolean} isApply - should we turn the style on or off?
 */
export const setFroalaTextStyle = (editor: any, style: string, value: string, isApply: boolean) => {
	const { format } = editor;
	switch (style) {
		case "strong":
			_forceSelectionToEditor(editor);
			if (isApply) {
				format.applyStyle("font-weight", "700");
			} else {
				format.applyStyle("font-weight", "400");
			}
			break;
		case "font-style":
			_forceSelectionToEditor(editor);
			if (isApply) {
				format.applyStyle("font-style", "italic");
			} else {
				format.applyStyle("font-style", "normal");
			}
			break;
		case "underline":
			editor.commands.underline();
			break;
		case "strikethrough":
			editor.commands.strikeThrough();
			break;
		case "text-align":
			editor.align.apply(value);
			break;
		case "color":
			_forceSelectionToEditor(editor);
			editor.colors.text(value);
			break;
		case "font-size":
			_forceSelectionToEditor(editor);
			editor.fontSize.apply(value);
			break;
		case "font-family":
			_forceSelectionToEditor(editor);
			editor.fontFamily.apply(value);
			break;
		case "font-weight":
			_forceSelectionToEditor(editor);
			format.applyStyle("font-weight", value);
			break;
		case "indent":
			editor.commands.indent();
			break;
		case "outdent":
			editor.commands.outdent();
			break;
		case "ul":
		case "ol":
			editor.lists.format(style.toUpperCase());
			break;
		case "link":

			// save selection before coloring the spans
			editor.selection.save();

			// highlight selected area that the link will be replacing
			const sel = window.getSelection() as Selection;
			const selectedArea = sel.getRangeAt(0);
			const selectedText = selectedArea.extractContents();
			const span = Object.assign(
				document.createElement("span"),
				{ id: HIGHLIGHT_ID },
			);
			span.style.backgroundColor = HIGHLIGHT_COLOR;
			span.appendChild(selectedText);
			selectedArea.insertNode(span);

			// restore selection after manipulating selected dom elements
			editor.selection.restore();

			// if there is no content, set no content to set an anchor for the popup
			if (!editor.html.get()) {
				editor.html.set("");
			}
			editor.link.showInsertPopup();

			// focus the popup
			const popup = getFroalaLinkPopupElement();
			popup.querySelector("input")
				.focus();

			break;
		case "paragraph":
			// Before modifying DOM, froala save current selection ,wrap the text into paragraphs
			// and restore selection , after manipulating DOM it does same to unwrap the text into paragraphs.
			// So using same pattern here to make sure it will not break any other froala operation.
			// Wrap.
			_forceSelectionToEditor(editor);
			editor.selection.save();
			editor.html.wrap(true, true, true);
			editor.selection.restore();
			// clear font-size before applying paragraph style
			const blocks = editor.selection.blocks();
			_.each(blocks, (block) => {
				_.each(block.querySelectorAll("*[style]"), ($el) => {
					const attr = $el.getAttribute("style") as string;
					if (attr.indexOf("font-size") >= 0) {
						$el.style.fontSize = "";
					}
				});
			});
			// Unwarp.
			editor.selection.save();
			editor.html.unwrap();
			editor.selection.restore();
			if (value) {
				editor.paragraphFormat.apply(value.toUpperCase());
			}
			break;
		default:
			break;
	}
};

/**
 * Find out if the editor is selected, if not, select it
 * @param editor {any} current Froala editor
 */
const _forceSelectionToEditor = (editor: any): void => {
	let el = editor.selection.get().focusNode;
	while (el) {
		if (el === editor.el) {
			return;
		}
		el = el.parentElement;
	}
	// editor not found in selection, so place selection at the end of current editor
	editor.selection.clear();
	editor.selection.setAtEnd(editor.el);
	editor.selection.restore();
};

/**
 * Calculate adjust handler for star shape.
 * @param newPosition {number} new position for adjust handler.
 * @param shape {IShape} Shape object.
 */
const _getAdjustHandlerPercentageForStar = (newPosition: number, shape: IShape) => {
	const { ext } = shape.spPr.xfrm;
	const vfShapeGuide = _.find(star5.avList, (shapeGuide) => shapeGuide.name === "vf");
	let newPositionPercentage = 0;
	if (vfShapeGuide) {
		const adjValue = newPosition;
		const vf = evaluateFormula(vfShapeGuide.fmla, []);
		const hd2 = ext.cy / 2;
		const svc = hd2 * vf / 100000;
		const ihd2 = svc - adjValue;
		newPositionPercentage = ihd2 * 50000 / svc;
	}
	return newPositionPercentage;
};

/**
 * Get adjust handler value for the specified position. This function provides a way to add custom calculation for
 * Adjust handler values.
 * Currently adjust handler can be calculated using following options:
 * * Reference dimension
 * * direction
 * * scale
 * This method should be only used if adjust handler can't be calculated using above options.
 * @param newPosition {number} new position for adjust handler.
 * @param shape {IShape} Shape object.
 */
export const getAdjustHandlerPercentage = (newPosition: number, shape: IShape) => {
	if (shape.spPr.geometry.type === GeometryTypes.PRESET) {
		switch (shape.spPr.geometry.presetShape) {
			case ShapeType.star5:
				return _getAdjustHandlerPercentageForStar(newPosition, shape);
			default:
				return 0;
		}
	}
	return 0;
};

/**
 * Return shape width or height considering shape rotation
 * @param pageObject {PageObjectType}
 * @return {number}
 */
export const getBoundingBox = (pageObject: PageObjectType) => {
	const objectDimensions = getDimensions(pageObject);
	const objectMaxX = _.maxBy(objectDimensions, (p: IPosition) => p.x) as IPosition;
	const objectMinX = _.minBy(objectDimensions, (p: IPosition) => p.x) as IPosition;
	const objectMaxY = _.maxBy(objectDimensions, (p: IPosition) => p.y) as IPosition;
	const objectMinY = _.minBy(objectDimensions, (p: IPosition) => p.y) as IPosition;
	return { width: objectMaxX.x - objectMinX.x, height: objectMaxY.y - objectMinY.y };
};

/**
 * Return distance between two objects
 * @param firstObject {PageObjectType}
 * @param secondObject {PageObjectType}
 * @return {IPosition}
 */
export const spaceBetweenTwoObjects = (firstObject: PageObjectType, secondObject: PageObjectType): IPosition => {
	const firstObjectDimensions = getDimensions(firstObject);
	const secondObjectDimensions = getDimensions(secondObject);
	const firstObjectMaxX = _.maxBy(firstObjectDimensions, (p: IPosition) => p.x) as IPosition;
	const secondObjectMinX = _.minBy(secondObjectDimensions, (p: IPosition) => p.x) as IPosition;
	const firstObjectMaxY = _.maxBy(firstObjectDimensions, (p: IPosition) => p.y) as IPosition;
	const secondObjectMinY = _.minBy(secondObjectDimensions, (p: IPosition) => p.y) as IPosition;
	return { x: Math.abs(firstObjectMaxX.x - secondObjectMinX.x), y: Math.abs(firstObjectMaxY.y - secondObjectMinY.y) };
};

/**
 * Returns true if given scale point type is one of the corner; false otherwise
 * @param scalePointType Scale point type
 */
export const isCornerScalePoint = (scalePointType: PageObjectScalePointType): boolean => {
	return (
		scalePointType === PageObjectScalePointType.CORNER_LEFT_BOTTOM
		|| scalePointType === PageObjectScalePointType.CORNER_LEFT_TOP
		|| scalePointType === PageObjectScalePointType.CORNER_RIGHT_BOTTOM
		|| scalePointType === PageObjectScalePointType.CORNER_RIGHT_TOP
	);
};

/**
 * Gets extents of cropping (or ghost) image
 * @param pageObject {IShape} page object
 * @returns {IExtents} extents of cropping image
 */
export const getImageExtents = (pageObject: IShape): IExtents => {
	const srcRect = (pageObject.spPr.fill as IPictureFillProperties).srcRect;
	const { cx, cy } = pageObject.spPr.xfrm.ext as IExtents;
	const lPx = srcRect.l * cx / PERCENTAGE_HUNDRED;
	const bPx = srcRect.b * cy / PERCENTAGE_HUNDRED;
	const rPx = srcRect.r * cx / PERCENTAGE_HUNDRED;
	const tPx = srcRect.t * cy / PERCENTAGE_HUNDRED;

	const imageWidth = cx - lPx - rPx;
	const imageHeight = cy - bPx - tPx;

	return { cx: imageWidth, cy: imageHeight };
};

/**
 * Gets present application state.
 * DO NOT MODIFY STATE MANUALLY. STATE SHOULD
 * ONLY BE MODIFIED VIA ACTIONS/REDUCERS
 * @param section {String} section of state to return
 * @returns {any} section of present application state,
 *                or entire state if no param passed
 */
export const getState = (section?: any): any => {
	const state: any = getStore()
		.getState();

	if (state) {
		if (section && state[section] && state[section][PRESENT]) {
			return state[section][PRESENT];
		} else {
			return state;
		}
	}
};

export const getShapeDrawMetadata = () => {
	const state: any = getState(SELECTION_STATE);
	let metadata;

	if (state && state[SHAPE_DRAW_METADATA]) {
		const rows = state[SHAPE_DRAW_METADATA][METADATA][TABLE_DIMENSIONS][ROWS];
		const columns = state[SHAPE_DRAW_METADATA][METADATA][TABLE_DIMENSIONS][COLUMNS];

		if (rows && columns) {
			metadata = {
				rows,
				columns,
			};
		}
	}
	return metadata;
};

// Reset present application state shape metadata.
export const resetShapeDrawMetadata = () => {
	const state: any = getState(SELECTION_STATE);

	if (state && state[SHAPE_DRAW_METADATA]) {
		state[SHAPE_DRAW_METADATA] = undefined;
	}
};

/* Gets extents of cropping (or ghost) image
* @param pageObject {IShape} page object
* @returns {IExtents} extents of cropping image
*/
export const convertShapeToCustom = (shape: IShape) => {
	const shapeGeometry = shape.spPr.geometry as IPresetGeometry;
	const geometry = presetGeometries[shapeGeometry.presetShape] as IPresetGeometry;
	const computedValues: any = {};
	const { cx, cy } = shape.spPr.xfrm.ext;
	// set the most independent values in computed values
	computedValues.h = cy;
	computedValues.w = cx;

	// compute predefined guides
	for (const guide of predefinedGuides) {
		computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
	}

	// compute adjust value list
	let computableAvList: IShapeGuide[] | undefined;
	if (shapeGeometry.avList) {
		computableAvList = shapeGeometry.avList;
	} else if (geometry.avList) {
		computableAvList = geometry.avList;
	}
	if (computableAvList) {
		for (const guide of computableAvList) {
			computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
		}
	}

	// compute guide list
	if (geometry.gdLst) {
		for (const guide of geometry.gdLst) {
			computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
		}
	}

	if (geometry.pathLst) {
		shape.spPr.geometry.pathLst = _.cloneDeep(geometry.pathLst);

		// for check marks, remove initial dummy path
		if (geometry.presetShape === ShapeType.checkmark) {
			const pathList = shape.spPr.geometry.pathLst as IShapePath[];
			pathList.splice(0, 1);
		}

		_instantiatePathObject(shape.spPr.geometry.pathLst, computedValues);
		if (shapeGeometry.presetShape === ShapeType.wedgeRectCallout) {
			_removeUnnecessaryPointsFromCallout(shape.spPr.geometry.pathLst);
		}
	}

	delete shapeGeometry.presetShape;
	delete shape.spPr.geometry.avList;

	const customGeometry = shape.spPr.geometry as ICustomGeometry;
	customGeometry.lineShape = true;
	shape.spPr.geometry.type = GeometryTypes.CUSTOM;
	return 0;
};

/**
 * Determine if p1, p2, and p3 are on a side of a Callout shape together
 * @param p1 {IShapePositionCoordinate} first point
 * @param p2 {IShapePositionCoordinate} second point
 * @param p3 {IShapePositionCoordinate} third point
 * @return {Boolean}
 */
const _shareSameLine = (
	p1: IShapePositionCoordinate, p2: IShapePositionCoordinate,
	p3: IShapePositionCoordinate,
) => {
	return (p1.x === p2.x && p2.x === p3.x) || (p1.y === p2.y && p2.y === p3.y);
};

/**
 * Remove points that do nothing
 * @param pathLst {IShapePath[]} Array of shape path
 */
const _removeUnnecessaryPointsFromCallout = (pathLst: IShapePath[]) => {
	for (const path of pathLst) {
		if (path.operations.length <= 4) {
			return;
		}

		let p1 = 0;
		let p2 = 1;
		let p3 = 2;

		while (p3 < path.operations.length - 1) {
			const op1 = path.operations[p1] as IDrawLineTo | IMovePathTo;
			const op2 = path.operations[p2] as IDrawLineTo | IMovePathTo;
			const op3 = path.operations[p3] as IDrawLineTo | IMovePathTo;

			if (_shareSameLine(op1.pt, op2.pt, op3.pt)) {
				path.operations.splice(p2, 1);
			} else {
				p1 += 1;
				p2 += 1;
				p3 += 1;
			}
		}
	}
};

/**
 * Fill in the placeholder path values with the computed values
 * @param pathLst {IShapePath[]} Array of shape path
 */
const _instantiatePathObject = (pathLst: IShapePath[], computedValues: any) => {
	for (const path of pathLst) {
		for (const pathOperation of path.operations) {
			switch (pathOperation.type) {
				case PathOperationTypes.MOVE_TO:
					pathOperation.pt.x = _getValue(pathOperation.pt.x, computedValues);
					pathOperation.pt.y = _getValue(pathOperation.pt.y, computedValues);
					break;
				case PathOperationTypes.LINE_TO:
					pathOperation.pt.x = _getValue(pathOperation.pt.x, computedValues);
					pathOperation.pt.y = _getValue(pathOperation.pt.y, computedValues);
					break;
				case PathOperationTypes.ARC_TO:
					pathOperation.wR = _getValue(pathOperation.wR, computedValues);
					pathOperation.hR = _getValue(pathOperation.hR, computedValues);
					pathOperation.stAng = _getValue(pathOperation.stAng, computedValues);
					pathOperation.swAng = _getValue(pathOperation.swAng, computedValues);
					break;
				case PathOperationTypes.CUBIT_BEZ_TO:
					const controlPoint1 = pathOperation.pts[0];
					const controlPoint2 = pathOperation.pts[1];
					const ePoint = pathOperation.pts[2];

					pathOperation.pts[0].x = _getValue(controlPoint1.x, computedValues);
					pathOperation.pts[0].y = _getValue(controlPoint1.y, computedValues);
					pathOperation.pts[1].x = _getValue(controlPoint2.x, computedValues);
					pathOperation.pts[1].y = _getValue(controlPoint2.y, computedValues);
					pathOperation.pts[2].x = _getValue(ePoint.x, computedValues);
					pathOperation.pts[2].x = _getValue(ePoint.y, computedValues);
					break;
				case PathOperationTypes.QUAD_BEZ_TO:
					const startPoint = pathOperation.pts[0];
					const endPoint = pathOperation.pts[1];

					pathOperation.pts[0].x = _getValue(startPoint.x, computedValues);
					pathOperation.pts[0].y = _getValue(startPoint.y, computedValues);
					pathOperation.pts[1].x = _getValue(endPoint.x, computedValues);
					pathOperation.pts[1].y = _getValue(endPoint.y, computedValues);
					break;
				case PathOperationTypes.CLOSE:
					const firstOperation = path.operations[0] as IMovePathTo;
					const closeOperation = pathOperation;
					const newOperation = {
						pt: { x: firstOperation.pt.x, y: firstOperation.pt.y },
						type: PathOperationTypes.LINE_TO,
					} as PathOperation;
					path.operations[path.operations.length - 1] = newOperation;
					path.operations.push(closeOperation);
					return;
				default:
					break;
			}
		}
	}
};

/**
 * call the c8 function to get the display name for the key
 * @param key {string} the key used to generate the display name
 * @returns {string} the display name provided by the c8 app
 */
export const getDisplayNameForKey: IDisplayNameFunc = (key: string): string => {
	const f: IDisplayNameFunc | null | undefined = _c8Functions && _c8Functions.getDisplayNameForKey;
	return f ? f(key) : key;
};

/**
 * call the c8 function to get the display name for the type
 * @param key {string} the key used to generate the display name
 * @returns {string} the display name provided by the c8 app
 */
export const getDisplayNameForType: IDisplayNameFunc = (key: string): string => {
	const f: IDisplayNameFunc | null | undefined = _c8Functions && _c8Functions.getDisplayNameForType;
	return f ? f(key) : key;
};

/**
 * call the c8 function to get the start type for the key
 * @param key {string} the key used to generate the display name
 * @returns {string} the start type for the key provided by the c8 app
 */
export const getStartTypeForKey: IDisplayNameFunc = (key: string): string => {
	const f: IDisplayNameFunc | null | undefined = _c8Functions && _c8Functions.getStartTypeForKey;
	return f ? f(key) : key;
};

/**
 * call the c8 function to get the localized string for the key
 * @param key {string} the key used to generate the display name
 * @returns {string} the display name provided by the c8 app
 */
export const localizeString: IDisplayNameFunc = (key: string): string => {
	const f: IDisplayNameFunc | null | undefined = _c8Functions && _c8Functions.localizeString;
	return f ? f(key) : key;
};

/**
 * call the c8 function to edit the attribute placeholder
 * @param key {string} the key for the placeholder
 */
export const displayCustomView: IDisplayCustomView =
	(attributeKeys: string[], groupObject: IGroupShape) => {
		if (_c8Functions && _c8Functions.displayCustomView) {
			_c8Functions.displayCustomView(attributeKeys, groupObject);
		}
	};

/**
 * call the c8 function to show attribute placeholder type instances
 * @param key {string} the key for the placeholder
 */
export const displayTypeInstances: IDisplayTypeInstancesFunc =
	(key: string | undefined) => {
		if (_c8Functions && _c8Functions.displayTypeInstances) {
			_c8Functions.displayTypeInstances(key);
		}
	};
